import { FC, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import PageHeader from '@/components/ui/PageHeader/PageHeader';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import { useAdminGradingPrompt, useUpdateGradingPrompt } from '@/api/useAdminGradingPrompts';
import Routes from '@/router/Routes';

interface CollapsibleSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection: FC<CollapsibleSectionProps> = ({ title, isOpen, onToggle, children }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-lg font-medium text-gray-900">{title}</span>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isOpen && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

const EditGradingPromptPage: FC = () => {
  const navigate = useNavigate();
  const { promptId } = useParams<{ promptId: string }>();
  const { data: prompt, isLoading } = useAdminGradingPrompt(promptId);
  const { mutate: updatePrompt, isPending: isSaving } = useUpdateGradingPrompt();

  const [promptOpen, setPromptOpen] = useState(true);
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [promptTemplate, setPromptTemplate] = useState('');

  useEffect(() => {
    if (prompt) {
      setPromptTemplate(prompt.prompt_template || '');
    }
  }, [prompt]);

  const handleBack = () => {
    navigate(Routes.ADMIN_GRADING_PROMPTS);
  };

  const handleSave = () => {
    if (!promptId) return;

    updatePrompt(
      {
        id: promptId,
        prompt_template: promptTemplate,
      },
      {
        onSuccess: () => {
          navigate(Routes.ADMIN_GRADING_PROMPTS);
        },
      }
    );
  };

  const templateVariables = [
    { name: '{{total_marks}}', description: 'Total marks for the question' },
    { name: '{{assessment_objectives}}', description: 'AOs being assessed' },
    { name: '{{levels}}', description: 'Formatted mark scheme levels' },
    { name: '{{indicative_content}}', description: 'Example content from mark scheme' },
    { name: '{{special_instructions}}', description: 'Special marking instructions' },
    { name: '{{question_content}}', description: 'The actual question text' },
    { name: '{{mark_scheme_json}}', description: 'Full mark scheme as JSON' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="loading loading-spinner loading-lg text-[#C87941]"></div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="text-gray-500">Grading prompt not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Header */}
      <PageHeader
        title=""
        showBackButton
        onBackClick={handleBack}
        rightContent={
          <LoadingButton
            loading={isSaving}
            onClick={handleSave}
            className="bg-[#0f172a] hover:bg-[#1e293b] text-white border-none px-6"
          >
            Save Changes
          </LoadingButton>
        }
      />

      {/* Prompt Info */}
      <div className="bg-white rounded-3xl mt-6 px-8 py-6">
        <div className="grid grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {prompt.question_categories.subject}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Paper</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900">
              {prompt.question_categories.paper_number}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-900 font-mono font-medium">
              {prompt.question_categories.category}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category Code</label>
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-500 font-mono text-sm">
              {prompt.category_code}
            </div>
          </div>
        </div>
      </div>

      {/* Template Variables Reference */}
      <div className="mt-6">
        <CollapsibleSection
          title="Template Variables"
          isOpen={variablesOpen}
          onToggle={() => setVariablesOpen(!variablesOpen)}
        >
          <div className="grid grid-cols-2 gap-3">
            {templateVariables.map((variable) => (
              <div
                key={variable.name}
                className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-xl"
              >
                <code className="text-sm font-mono text-[#C87941]">{variable.name}</code>
                <span className="text-sm text-gray-600">{variable.description}</span>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      </div>

      {/* Prompt Template Section */}
      <div className="mt-6">
        <CollapsibleSection
          title="Prompt Template"
          isOpen={promptOpen}
          onToggle={() => setPromptOpen(!promptOpen)}
        >
          <textarea
            value={promptTemplate}
            onChange={(e) => setPromptTemplate(e.target.value)}
            rows={25}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C87941] focus:border-transparent font-mono text-sm"
            placeholder="Enter the grading prompt template..."
          />
          <p className="mt-2 text-sm text-gray-500">
            Use template variables like {'{{total_marks}}'} to inject dynamic content when grading.
            Variables are replaced with actual values from the question and mark scheme.
          </p>
        </CollapsibleSection>
      </div>
    </div>
  );
};

export default EditGradingPromptPage;
